import React from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import FareSimpleSorting from '../../sorting/FareSimpleSorting';
import BudgetFilter from '../Budget';
import StopsFilter from '../StopsFilter';
import TravelClassFilter from './TravelClassFilter';
import FlightTypeFilter from './FlightTypeFilterMobile';
import OriginDestinationFilter from './OriginDestinationFilterMobile';
import NavigationHeader from '../../elements/NavigationHeader';
import AirlinesFilter from './AirlinesFilter';
import TravelerInterest from '../InlineFilters/TravelerInterest';
import {
  enterFullMode,
  exitFullMode,
  notifyFullMode,
  notifyExitFullMode
} from '../../containers/_commons/navigation';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import { isNotNil, isNilOrEmpty } from '../../../helpers/utils';
import SingleDatePicker from '../DatePickerWidget/SingleDatePicker';
import SingleDateRangePicker from '../DatePickerWidget/SingleDateRangePicker';

const InlineFiltersMobile = ({
  clearButtonDisplay,
  sortingDisplay,
  showLabels,
  locations,
  dateRange,
  budget: { currencyCode, ...budget },
  travelClass,
  flightType,
  stops,
  sorting,
  onClear,
  availableFilters,
  containerSettings,
  clearAllSettings: { showIcon, icon: clearAllIcon, ...clearAllSettings },
  clearAllColumnSettings,
  containerOnFullScreenSettings,
  originDestinationSettings,
  travelClassSettings,
  travelClassColumnSettings,
  flightTypeSettings,
  flightTypeColumnSettings,
  dateRangeSettings,
  dateRangeColumnSettings,
  stopSettings,
  stopColumnSettings,
  budgetSettings,
  budgetColumnSettings,
  sortingSettings,
  sortingColumnSettings,
  sortingRowSettings,
  children,
  headerLocation,
  header,
  headerSettings,
  dialogSettings,
  enableExperimentalNavigation,
  airlinesSettings,
  airlines,
  travelerInterest,
  travelerInterestSettings,
  travelerInterestColumnSettings,
  ...props
}) => {
  const { translate } = React.useContext(Context);
  const ref = React.useRef();
  const [dirtyDateSelector, setDirtyDateSelector] = React.useState(false);
  const isDirty = !!(
    locations.origin.value ||
    locations.destination.value ||
    dateRange.value.start ||
    dateRange.value.end ||
    dirtyDateSelector === true ||
    isNotNil(budget.value) ||
    !isNilOrEmpty(travelerInterest.selectedItems)
  );
  const useClassicNavigation = !enableExperimentalNavigation;

  const [navigation, setNavigation] = React.useState('all');
  const [isFullMode, setIsFullMode] = React.useState(false);

  const goBack = () => {
    setNavigation('all');
    notifyExitFullMode();
    if (useClassicNavigation) {
      exitFullMode(ref);
      setIsFullMode(false);
    }
  };

  const getNavigationProps = (
    key,
    { onClear, onFocus, onChange, onClick, avoidCloseFilter = false, ...rest }
  ) => {
    return {
      onClear: (event) => {
        if (onClear) {
          onClear(event);
        }
        setNavigation(key);
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation && !isFullMode) {
          enterFullMode(ref);
          setIsFullMode(true);
        }
      },
      onFocus: (event) => {
        if (onFocus) {
          onFocus(event);
        }
        setNavigation(key);
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation && !isFullMode) {
          enterFullMode(ref);
          setIsFullMode(true);
        }
      },
      onClick: (event) => {
        if (onClick) {
          onClick(event);
        }
        setNavigation(key);
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation && !isFullMode) {
          enterFullMode(ref);
          setIsFullMode(true);
        }
      },
      onChange: (event) => {
        if (onChange) {
          onChange(event);
        }
        if (!Array.isArray(event)) {
          if (!avoidCloseFilter) {
            goBack();
          }
        }
      },
      ...rest
    };
  };

  const elementsWrappers = {
    Airlines: (e) => <Col key="Airlines">{e}</Col>,
    OriginDestination: (e) => {
      return e;
    },
    SingleDatePicker: (e) => (
      <Col key="SingleDatePicker" {...dateRangeColumnSettings}>
        {e}
      </Col>
    ),
    SingleDateRangePicker: (e) => (
      <Col key="SingleRangeDatePicker" {...dateRangeColumnSettings}>
        {e}
      </Col>
    ),
    TravelClass: (e) => (
      <Col xs="12" key="TravelClass" {...travelClassColumnSettings}>
        {e}
      </Col>
    ),
    FlightType: (e) => (
      <Col xs="12" key="FlightType" {...flightTypeColumnSettings}>
        {e}
      </Col>
    ),
    Stops: (e) => (
      <Col xs="12" key="Stops" {...stopColumnSettings}>
        {e}
      </Col>
    ),
    Budget: (e) => (
      <Col xs="12" key="Budget" showLabel={showLabels} {...budgetColumnSettings}>
        {e}
      </Col>
    ),
    Sorting: (e) => (
      <Col xs="12" key="Sorting" {...sortingColumnSettings}>
        {e}
      </Col>
    ),
    TravelerInterest: (e) => (
      <Col xs="12" key="travelerInterest" textAlign="right" {...travelerInterestColumnSettings}>
        {e}
      </Col>
    ),
    ClearButton: (e) => (
      <Col xs="12" key="clearAllBtn" textAlign="right" {...clearAllColumnSettings}>
        {e}
      </Col>
    )
  };

  const handleClear = () => {
    onClear();
    if (elementRefDatePicker.current?.reset !== undefined) {
      elementRefDatePicker.current.reset(false);
    }
  };

  const elementRefDatePicker = React.useRef(null);

  const elements = {
    Airlines: (
      // <Col key="Airlines">
      <AirlinesFilter
        {...airlinesSettings}
        {...airlines}
        isMobile
        {...getNavigationProps('Airlines', airlines)}
        origin={getNavigationProps('Origin', locations.origin)}
        destination={getNavigationProps('Destination', locations.destination)}
      />
      // </Col>
    ),
    OriginDestination: (
      <OriginDestinationFilter
        key="OriginDestination"
        navigation={navigation}
        showLabels={showLabels}
        {...originDestinationSettings}
        {...locations}
        origin={getNavigationProps('Origin', locations.origin)}
        destination={getNavigationProps('Destination', locations.destination)}
        isFullMode={isFullMode}
      />
    ),
    SingleDatePicker: (
      // <Col key="SingleDatePicker" {...dateRangeColumnSettings}>
      <SingleDatePicker
        showLabel={showLabels}
        {...dateRangeSettings}
        {...dateRange}
        onChange={(date) =>
          dateRange.onChange({
            start: date.start,
            end: date.end,
            type: 'departure',
            flexibleDates: date.flexibleDates,
            availableDays: date.availableDays,
            checkedFlexibleDates: date.checkedFlexibleDates
          })
        }
        setDirtyDateSelector={setDirtyDateSelector}
        defaultValue={dateRange.value.start}
        isMobile={true}
      />
      // </Col>
    ),
    SingleDateRangePicker: (
      <SingleDateRangePicker
        showLabel={showLabels}
        {...dateRangeSettings}
        {...dateRange}
        onChange={(date) => {
          dateRange.onChange({
            start: date.start,
            end: date.end,
            type: 'departure',
            flexibleDates: date.flexibleDates,
            availableDays: date.availableDays,
            checkedFlexibleDates: date.checkedFlexibleDates
          });
        }}
        defaultValue={dateRange.value}
        isMobile={true}
      />
    ),
    TravelClass: (
      // <Col xs="12" key="TravelClass" {...travelClassColumnSettings}>
      <TravelClassFilter
        isMobile
        showLabel={showLabels}
        {...travelClassSettings}
        {...getNavigationProps('TravelClass', {
          ...travelClass,
          avoidCloseFilter: travelClassSettings.enabledFooter === true ? true : false
        })}
        isFullMode={isFullMode}
        goBack={goBack}
      />
      // </Col>
    ),
    FlightType: (
      // <Col xs="12" key="FlightType" {...flightTypeColumnSettings}>
      <FlightTypeFilter showLabel={showLabels} {...flightTypeSettings} {...flightType} />
      // </Col>
    ),
    Stops: (
      //   <Col xs="12" key="Stops" {...stopColumnSettings}>
      <StopsFilter showLabel={showLabels} {...stopSettings} {...stops} dataTestId="stopsFilter" />
      // </Col>
    ),
    Budget: (
      // <Col xs="12" key="Budget" showLabel={showLabels} {...budgetColumnSettings}>
      <BudgetFilter currencyCode={currencyCode} {...budget} {...budgetSettings} />
      // </Col>
    ),
    Sorting: (
      // <Col xs="12" key="Sorting" {...sortingColumnSettings}>
      <FareSimpleSorting {...sortingSettings} {...sorting} dataTestId="fareSimpleSorting" />
      // </Col>
    ),
    TravelerInterest: (
      // <Col xs="12" key="travelerInterest" textAlign="right" {...travelerInterestColumnSettings}>
      <TravelerInterest
        isMobile
        {...travelerInterest}
        // {...travelerInterestSettings}
        {...getNavigationProps('TravelerInterest', travelerInterest)}
      />
      // </Col>
    ),
    ClearButton: (
      // <Col xs="12" key="clearAllBtn" textAlign="right" {...clearAllColumnSettings}>
      <Button
        paddingWidth="0"
        textTransform="uppercase"
        fontSize="3"
        onClick={handleClear}
        disabled={!isDirty}
        cursor={isDirty ? 'pointer' : 'default'}
        dataTestId="clearAllBtn"
        display="inline-flex"
        alignItems="center"
        ariaLabel={translate('cmp-filter-clear-button-accessibility-message', 'Clear all')}
        {...clearAllSettings}
      >
        {translate('cmp-filter-clear', 'Clear all')}
        {showIcon && <Icon icon="close" paddingLeft="1" size={18} {...clearAllIcon} />}
      </Button>
      // </Col>
    )
  };

  let fullFiltersList = availableFilters;
  if (clearButtonDisplay === 'end') {
    fullFiltersList = [...availableFilters, 'ClearButton'];
  } else if (clearButtonDisplay === 'start') {
    fullFiltersList = ['ClearButton', ...availableFilters];
  }

  const filtersToDisplay = fullFiltersList.filter((e) => {
    let iterator = e;
    if (typeof iterator === 'object') {
      iterator = e.type;
    }
    return elements[iterator] && (sortingDisplay === 'inline' || iterator !== 'Sorting');
  });

  const [classNameDialog, styleDialog] = useStyles(dialogSettings);
  const myStyle = Object.assign(
    {
      width: '100vw',
      height: '100vh',
      padding: 0,
      margin: 0,
      overflow: 'hidden',
      background: 'white'
    },
    styleDialog
  );

  const overlayStyle = {
    zIndex: 999999,
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
    overflow: 'auto'
  };

  if (navigation !== 'all' && enableExperimentalNavigation) {
    return (
      <DialogOverlay style={overlayStyle}>
        <DialogContent className={classNameDialog} style={myStyle}>
          {/* FIXME: Conditional styles for DateRangeSelector, the new component should not need this. */}
          <Box position={navigation === 'all' ? 'relative' : undefined} height="100" {...props}>
            <NavigationHeader
              headerLocation={headerLocation}
              title={translate(`cmp-header-mobile-${navigation.toLowerCase()}`)}
              onClickIcon={goBack}
              styleIcon={{
                transform: 'rotate(90deg)',
                display: 'inline-block'
              }}
              {...headerSettings}
            />
            <Row gutters {...containerSettings} {...containerOnFullScreenSettings}>
              {navigation === 'Origin' || navigation === 'Destination'
                ? elements.OriginDestination
                : elements[navigation]}
            </Row>
          </Box>
        </DialogContent>
      </DialogOverlay>
    );
  }

  const wrapperRender = elementsWrappers[navigation];

  if (navigation === 'TravelerInterest' || navigation === 'Airlines') {
    props = {
      ...props,
      'cmp-styles': {
        height: 'calc(100% - 67px) !important',
        'overflow-y': 'hidden !important'
      }
    };
  }
  return (
    <Box position={navigation === 'all' ? 'relative' : undefined} focusReferences={ref} {...props}>
      {navigation === 'all' ? (
        <>
          {header}
          <Row gutters marginBottom="2" {...containerSettings}>
            {filtersToDisplay.map((ele) => {
              let e = ele;
              if (typeof e === 'object') {
                e = e.type;
              }
              const wrapper = elementsWrappers[e];
              if (typeof ele === 'object' && ele.type === 'TravelerInterest') {
                const temporalElement = React.cloneElement(elements[ele.type], {
                  ...travelerInterestSettings[ele.id]
                });
                return wrapper(temporalElement);
              }
              if (ele === 'SingleDatePicker' || ele === 'SingleDateRangePicker') {
                const temporalElement = React.cloneElement(elements[ele], {
                  ref: elementRefDatePicker
                });
                return wrapper(temporalElement);
              }
              const element = wrapper(elements[e]);
              return element;
            })}
          </Row>
          {sortingDisplay === 'below-filters' && (
            <Row gutters marginBottom="2" {...sortingRowSettings}>
              {elementsWrappers['Sorting'](elements['Sorting'])}
            </Row>
          )}
          {children}
        </>
      ) : (
        <>
          <NavigationHeader
            headerLocation={headerLocation}
            title={translate(`cmp-header-mobile-${navigation.toLowerCase()}`)}
            onClickIcon={goBack}
            styleIcon={{
              transform: 'rotate(90deg)',
              display: 'inline-block'
            }}
            {...headerSettings}
          />
          <Row
            gutters
            {...containerSettings}
            paddingWidth="15px !important"
            {...containerOnFullScreenSettings}
          >
            {navigation === 'Origin' || navigation === 'Destination'
              ? elements.OriginDestination
              : navigation === 'TravelerInterest'
              ? filtersToDisplay
                  .filter((ele) => ele.type === 'TravelerInterest')
                  .map((ele) => {
                    let e = ele;
                    if (typeof e === 'object') {
                      e = e.type;
                    }
                    const wrapper = elementsWrappers[e];
                    if (typeof ele === 'object' && ele.type === 'TravelerInterest') {
                      const temporalElement = React.cloneElement(elements[ele.type], {
                        ...travelerInterestSettings[ele.id],
                        navigation: true,
                        goBack
                      });
                      return wrapper(temporalElement);
                    }
                    return null;
                  })
              : navigation === 'Airlines'
              ? filtersToDisplay
                  .filter((ele) => ele === 'Airlines')
                  .map((ele) => {
                    const wrapper = elementsWrappers[ele];
                    const temporalElement = React.cloneElement(elements[ele], {
                      navigation: true,
                      goBack
                    });
                    return wrapper(temporalElement);
                  })
              : wrapperRender(elements[navigation])}
          </Row>
        </>
      )}
    </Box>
  );
};

InlineFiltersMobile.propTypes = {
  clearButtonDisplay: PropTypes.oneOf(['start', 'end', 'hidden']),
  sortingDisplay: PropTypes.oneOf(['inline', 'below-filters']),
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(
    PropTypes.oneOf([
      'OriginDestination',
      'SingleDatePicker',
      'SingleDateRangePicker',
      'TravelClass',
      'Stops',
      'Budget',
      'FlightType',
      'Sorting',
      'Airlines',
      'TravelerInterest'
    ])
  ),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  containerOnFullScreenSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  originDestinationSettings: PropTypes.objectOf(PropTypes.any),
  travelClassSettings: PropTypes.objectOf(PropTypes.any),
  travelClassColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeColumnSettings: PropTypes.objectOf(PropTypes.any),
  dateRangeSettings: PropTypes.objectOf(PropTypes.any),
  dateRangeColumnSettings: PropTypes.objectOf(PropTypes.any),
  stopSettings: PropTypes.objectOf(PropTypes.any),
  stopColumnSettings: PropTypes.objectOf(PropTypes.any),
  budgetSettings: PropTypes.objectOf(PropTypes.any),
  budgetColumnSettings: PropTypes.objectOf(PropTypes.any),
  sortingSettings: PropTypes.objectOf(PropTypes.any),
  sortingColumnSettings: PropTypes.objectOf(PropTypes.any),
  sortingRowSettings: PropTypes.objectOf(PropTypes.any),
  headerSettings: PropTypes.objectOf(PropTypes.any),
  dialogSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),
  dateRange: PropTypes.shape({
    value: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    defaultValue: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    onChange: PropTypes.func,
    onClear: PropTypes.func
  }),
  budget: PropTypes.shape({
    value: PropTypes.number,
    defaultValue: PropTypes.number,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    currencyCode: PropTypes.string
  }),
  travelClass: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  flightType: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  stops: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func
  }),
  sorting: PropTypes.shape({
    selectedOption: PropTypes.string,
    onChange: PropTypes.func
  }),
  onClear: PropTypes.func,
  children: PropTypes.node,
  header: PropTypes.node,
  headerLocation: PropTypes.string,
  enableExperimentalNavigation: PropTypes.bool
};

InlineFiltersMobile.defaultProps = {
  clearButtonDisplay: 'hidden',
  sortingDisplay: 'inline',
  showLabels: false,
  availableFilters: ['OriginDestination', 'SingleDateRangePicker', 'Budget'],
  containerSettings: {
    padding: '1'
  },
  containerOnFullScreenSettings: {},
  originDestinationSettings: {},
  travelClassSettings: {},
  travelClassColumnSettings: {},
  flightTypeSettings: {},
  flightTypeColumnSettings: {},
  dateRangeSettings: {},
  dateRangeColumnSettings: {},
  stopSettings: {
    uiDisplay: {
      showLabel: false
    },
    height: '100',
    components: {
      select: {
        height: '100',
        selectStyles: { height: '100%' }
      }
    }
  },
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    showIcon: true,
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  clearAllColumnSettings: {},
  stopColumnSettings: {},
  budgetSettings: {},
  budgetColumnSettings: {},
  sortingSettings: {},
  sortingColumnSettings: {},
  sortingRowSettings: {},
  locations: { origin: {}, destination: {} },
  travelClass: {},
  flightType: {},
  dateRange: {},
  budget: {},
  stops: {},
  sorting: {},
  children: null,
  headerLocation: undefined,
  header: null,
  dialogSettings: {},
  onClear: null,
  enableExperimentalNavigation: false
};

export default InlineFiltersMobile;
